/**
 * R2-09 (0.9.40 rc2): "Did you mean name@gmail.com?" under an email box, for a likely typo of a common provider. The same list
 * and the same rule as the server's `Donors\EmailCheck::suggest()`: a domain one or two letters from a common provider, or a
 * common provider with a mistyped ending. It only offers; the donor chooses. The server checks the address itself.
 */

export const COMMON_DOMAINS = [
	'gmail.com',
	'yahoo.com',
	'hotmail.com',
	'outlook.com',
	'icloud.com',
	'aol.com',
	'comcast.net',
	'msn.com',
	'live.com',
	'me.com',
	'verizon.net',
	'att.net',
	'sbcglobal.net',
	'ymail.com',
	'protonmail.com',
	'proton.me',
	'mail.com',
	'gmx.com',
	'yahoo.co.uk',
	'hotmail.co.uk',
];

/**
 * The number of single-letter edits between two strings.
 *
 * @param {string} a
 * @param {string} b
 * @return {number}
 */
function distance( a, b ) {
	const row = Array.from( { length: b.length + 1 }, ( _, i ) => i );

	for ( let i = 1; i <= a.length; i++ ) {
		let previous = row[ 0 ];

		row[ 0 ] = i;

		for ( let j = 1; j <= b.length; j++ ) {
			const kept = row[ j ];

			row[ j ] = Math.min( row[ j ] + 1, row[ j - 1 ] + 1, previous + ( a[ i - 1 ] === b[ j - 1 ] ? 0 : 1 ) );
			previous = kept;
		}
	}

	return row[ b.length ];
}

/**
 * A likely intended address, or '' when there is none.
 *
 * @param {string} email
 * @return {string}
 */
export function suggestEmail( email ) {
	const value = String( email || '' ).trim().toLowerCase();
	const at = value.lastIndexOf( '@' );

	if ( at < 1 ) {
		return '';
	}

	const local = value.slice( 0, at );
	const domain = value.slice( at + 1 );

	if ( '' === domain || COMMON_DOMAINS.includes( domain ) ) {
		return '';
	}

	let best = '';
	let shortest = 3;

	COMMON_DOMAINS.forEach( ( common ) => {
		const d = distance( domain, common );

		if ( d < shortest ) {
			best = common;
			shortest = d;
		}
	} );

	const parts = /^([a-z0-9-]+)\.([a-z]{1,4})$/.exec( domain );

	if ( '' === best && parts ) {
		best = COMMON_DOMAINS.find( ( common ) => common.startsWith( `${ parts[ 1 ] }.` ) && distance( parts[ 2 ], common.slice( parts[ 1 ].length + 1 ) ) <= 2 ) || '';
	}

	return '' === best ? '' : `${ local }@${ best }`;
}

/**
 * Adds the offer under an email box: shown when the typed address looks like a typo, a button that puts the suggestion in.
 *
 * @param {HTMLInputElement} input
 * @param {(text: string) => string} __ Translates a sentence.
 * @return {HTMLElement} The offer, already placed after the box.
 */
export function attachEmailSuggestion( input, __ ) {
	const offer = document.createElement( 'p' );
	const button = document.createElement( 'button' );

	offer.className = 'anudan-email-suggestion';
	offer.hidden = true;
	offer.setAttribute( 'aria-live', 'polite' );
	button.type = 'button';
	button.className = 'anudan-email-suggestion__button';
	offer.appendChild( button );
	input.insertAdjacentElement( 'afterend', offer );

	const update = () => {
		const suggestion = suggestEmail( input.value );

		offer.hidden = '' === suggestion;
		button.textContent = '' === suggestion ? '' : __( 'Did you mean %s?' ).replace( '%s', suggestion );
		button.dataset.suggestion = suggestion;
	};

	input.addEventListener( 'blur', update );
	input.addEventListener( 'input', () => {
		if ( ! offer.hidden ) {
			update();
		}
	} );
	button.addEventListener( 'click', () => {
		input.value = button.dataset.suggestion || input.value;
		input.dispatchEvent( new Event( 'input', { bubbles: true } ) );
		offer.hidden = true;
		input.focus();
	} );

	return offer;
}
